import React, { useState, useEffect } from "react";
import {RiEditLine,RiCloseLine,RiSaveLine,RiErrorWarningLine,RiInformationLine,RiMoneyDollarCircleLine,RiTicketLine,RiUserLine} from "react-icons/ri";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";

const BusMarkupModal = ({
isOpen,
  onClose,
  booking,
  onMarkupUpdate,
  title = "Update Markup",
  buttonText = "Update"
}) => {
  const [markup, setMarkup] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (booking && isOpen) {
      setMarkup(booking.markup?.toString() || "");
      setError("");
      setLoading(false);
    }
  }, [booking, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setMarkup("");
        setError("");
        setLoading(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const formatCurrency = (amount) => {
    return `INR ${amount}`;
  };

  const calculateCustomerPayable = (data) => {
    if (!data) return 0;
    const totalFare = data.totalFare || 0;
    const convenienceFee = data.convenienceFee || 0;
    const paymentGatewayAmount = data.paymentGatewayAmount || 0;
    const discount = data.discount || 0;
    return totalFare + convenienceFee + paymentGatewayAmount - discount;
  };

  const handleSubmit = async () => {
    if (!markup || markup.trim() === "") {
      setError("Please enter a markup amount");
      return;
    }

    const markupValue = parseFloat(markup);
    if (isNaN(markupValue) || markupValue < 0) {
      setError("Please enter a valid positive number");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await apiService.put(
        `${config.UPDATEBUSMARKUP}`,
        {
          bookingId: booking.busBookingId,
          markup: markupValue
        }
      );

      if (response && response.succeeded) {
        setLoading(false);
        if (onMarkupUpdate && response.data) {
          onMarkupUpdate(response.data);
        }
        // Close modal immediately after successful update
        onClose();
      } else {
        setError(response.message || "Failed to update markup");
        setLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update markup");
      console.error("Markup update error:", err);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError("");
    setLoading(false);
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleSubmit();
    }
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const currentTotalPayable = calculateCustomerPayable(booking);

  return (
    <div
      className="modal fade show d-block"
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1050,
        display: 'block'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) {
          handleClose();
        }
      }}
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '480px' }}>
        <div className="modal-content shadow" style={{ borderRadius: '12px' }}>

          {/* Header - White with dark text */}
          <div className="modal-header border-0 pb-0 pt-3 px-4">
            <h6 className="modal-title d-flex align-items-center gap-2 text-dark">
              <RiEditLine className="text-primary" />
              <span>{title}</span>
            </h6>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              disabled={loading}
            />
          </div>

          {/* Body */}
          <div className="modal-body px-4 py-3">
            {/* Booking Info */}
            <div className="d-flex justify-content-between bg-light rounded p-2 mb-3">
              <span className="text-muted small">
                <RiTicketLine className="me-1" /> {booking?.bookingNo || "N/A"}
              </span>
              <span className="text-muted small">
                <RiUserLine className="me-1" /> {booking?.passengerName || "N/A"}
              </span>
            </div>

            {/* Current Total */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted small">Current Total</span>
              <span className="fw-semibold">{formatCurrency(currentTotalPayable)}</span>
            </div>

            {/* Current Markup */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="text-muted small">Current Markup</span>
              <span className="text-warning fw-semibold">{formatCurrency(booking?.markup || 0)}</span>
            </div>

            {/* Input */}
            <div className="mb-3">
              <label className="form-label small fw-semibold">New Markup Amount</label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <RiMoneyDollarCircleLine className="text-primary" />
                </span>
                <input
                  type="number"
                  className={`form-control form-control-sm ${error ? 'is-invalid' : ''}`}
                  placeholder="Enter amount"
                  value={markup}
                  onChange={(e) => {
                    setMarkup(e.target.value);
                    setError("");
                  }}
                  onKeyDown={handleKeyPress}
                  step="0.01"
                  min="0"
                  disabled={loading}
                  autoFocus
                />
                {error && (
                  <div className="invalid-feedback small">
                    <RiErrorWarningLine className="me-1" />
                    {error}
                  </div>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="bg-warning bg-opacity-10 rounded p-2">
              <small className="text-muted d-flex align-items-start gap-1">
                <RiInformationLine className="mt-1 flex-shrink-0" />
                <span>Markup added updated Price see in customer invoice </span>
              </small>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer border-0 pt-0 px-4 pb-3">
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary px-4"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" />
                  Updating...
                </>
              ) : (
                <>
                  <RiSaveLine className="me-1" /> {buttonText}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .modal.fade.show {
          display: block;
        }
        
        .modal-content {
          border: none;
        }
        
        .bg-opacity-10 {
          --bs-bg-opacity: 0.1;
        }
        
        .form-control-sm {
          font-size: 0.875rem;
        }
        
        .form-control:focus {
          border-color: #0d6efd;
          box-shadow: 0 0 0 0.15rem rgba(13, 110, 253, 0.15);
        }
        
        .form-control.is-invalid:focus {
          border-color: #dc3545;
          box-shadow: 0 0 0 0.15rem rgba(220, 53, 69, 0.15);
        }
      `}</style>
    </div>
  );
};

export default BusMarkupModal;